export const GAME_VERSION = "m1.4.0";
export const SIM_HZ = 60;
export const SHIFT_TICKS = 180 * SIM_HZ;
export const LOAD_WINDOW_TICKS = 3 * SIM_HZ;
export const CHAIN_WINDOW_TICKS = 10 * SIM_HZ;
export const CRITICAL_WINDOW_TICKS = 8 * SIM_HZ;

/**
 * Locked overflow thresholds (LOCKED-DESIGN Section 7). This module is the
 * single owner: simulation, HUD, and audio must consume these constants
 * rather than restating the numbers.
 * - Capacity 36 starts the eight-second critical alarm.
 * - Warning states escalate at 24 and 30 units.
 * - Critical clears only when total bulk falls to OVERFLOW_SEVERE or below.
 */
export const FACILITY_CAPACITY = 36;
export const OVERFLOW_WARNING = 24;
export const OVERFLOW_SEVERE = 30;

/**
 * Portal re-arm cooldown (LOCKED-DESIGN 3.4 shared traversal rule, 18.10
 * kernel rider). After a subject — the player or one specific trash body —
 * traverses a portal it cannot traverse ANY portal again for this many fixed
 * ticks.
 *
 * 24 ticks = 0.4s at 60Hz, the centre of the sanctioned 18-30 tick band:
 * - Long enough to kill the dogfood failure — exiting a portal and stepping
 *   straight back could instantly re-loop, because the old re-arm gate only
 *   required leaving the portal volume once, which one step satisfies. A
 *   reversing step cannot re-enter the disc inside 0.4s, so the loop dies.
 * - Short enough to never disturb normal routing: a delivered piece traverses
 *   exactly once (arena -> chamber) and is gone well before the window
 *   matters, and a deliberate recovery re-entry after reorienting always
 *   outlasts 0.4s. It only ever blocks a SECOND crossing by the SAME subject.
 *
 * Deterministic sim state keyed to tick: pure and replay-reproducible, with no
 * wall-clock and no cosmetic RNG.
 */
export const PORTAL_REARM_TICKS = 24;

/**
 * Jam-recovery timers (LOCKED-DESIGN 18.12 / m1.3.0 feed-the-maw redesign).
 * These are the STANDARD-mode defaults; the kernel reads seed `rules`
 * overrides for Simple (dwellTicks/overloadTicks — the sanctioned m1.3.0
 * kernel-reads-rules change). FEED_GRACE_TICKS is a fixed fairness valve, NOT a
 * seed dial.
 * - DWELL_TICKS 540 = 9s: the chamber cooks the player after this on entry
 *   (always runs — the chamber is never safe to loiter). 720 (12s) on Simple.
 * - DWELL_WARNING_TICKS 120 = 2s: derivable "COOKING" window before dwell
 *   expiry (emits no ledger event; presentation polls getters).
 * - OVERLOAD_TICKS 1800 = 30s: continuous any-lane jam without a full clear
 *   overloads the furnace (run ends). 2400 (40s) on Simple.
 * - FEED_GRACE_TICKS 480 = 8s: past the overload deadline the run only ends if
 *   no drained feed landed within this window — a player mid-scoop is not
 *   neglecting the jam. Fixed constant, never seed-parameterized.
 * - CLEAR_RETURN_GRACE_TICKS 240 = 4s: when the LAST jam clears while the player
 *   is still in the chamber, dwell is topped up to at least this window so a
 *   late-landing final feed can never cook a player who just finished the
 *   recovery (LOCKED-DESIGN 18.12 return-grace). Extends dwell only, never
 *   shortens — past the window the chamber still cooks (no free loiter).
 */
export const DWELL_TICKS = 540;
export const DWELL_WARNING_TICKS = 120;
export const OVERLOAD_TICKS = 1800;
export const FEED_GRACE_TICKS = 480;
export const CLEAR_RETURN_GRACE_TICKS = 240;

export type TrashKind = "crate" | "can" | "green" | "red" | "husk";
export type ReceiverTier = "safe" | "technical" | "hazard";
export type ArenaAnchor = "floor" | "wall" | "ramp" | "island" | "precision" | "rail";
export type LoadOutcome = "clean" | "partial" | "missed";
export type RunEndReason = "complete" | "overflow" | "abandoned" | "cooked" | "jam-overload";

export interface Route {
  anchor: ArenaAnchor;
  receiver: ReceiverTier;
}

export interface SpawnSpec {
  tick: number;
  chute: "left" | "center" | "right";
  kind: Exclude<TrashKind, "husk">;
}

export interface TicketSpec {
  id: string;
  phase: number;
  startsAt: number;
  expiresAt: number;
  recipe: Partial<Record<Exclude<TrashKind, "husk">, number>>;
  receiver?: ReceiverTier;
}

export interface PhaseSpec {
  index: number;
  label: string;
  startsAt: number;
  activeAnchors: ArenaAnchor[];
  activeReceivers: ReceiverTier[];
  conveyor: "forward" | "reverse";
  closedLane?: "left" | "right";
}

export interface GameSeed {
  format: 1;
  id: string;
  version: string;
  title: string;
  description: string;
  rngSeed: number;
  phases: PhaseSpec[];
  spawns: SpawnSpec[];
  tickets: TicketSpec[];
  /**
   * Unranked presentation ruleset (LOCKED-DESIGN 18.11 Simple mode). Pure data
   * field — the Simulation step path never reads it, so it carries no version
   * bump and no kernel change. Omitted on the three official ranked seeds so
   * their serialized bundles stay byte-identical; "simple" marks a kid-mode
   * seed whose validateSeed skips the ranked pressure-curve gate for the honest
   * Simple band (validation.ts). Chain/Grade/tickets still run in the sim; the
   * ruleset only governs presentation and validation, never behavior.
   */
  ruleset?: "official" | "simple";
  /**
   * Presentation + jam-timer ruleset data (LOCKED-DESIGN 18.11 Simple v2 +
   * 18.12 m1.3.0). Omitted entirely on the three official ranked seeds so their
   * serialized bundles stay byte-identical (a NO-version-bump guarantee for the
   * presentation-only fields, asserted in tests). Only SIMPLE_SEEDS set it.
   *
   * IMPORTANT (m1.3.0 change): `dwellTicks`/`overloadTicks` ARE kernel-read —
   * this is the sanctioned m1.3.0 exception to the old "kernel never reads
   * rules" invariant. The remaining fields stay presentation-only:
   * - `beamCapacity`: max bulk the gravity beam holds in this run (Simple = 6;
   *   LOCKED-DESIGN 18.15 restores the standard beam, so 18.11 v2's 3-bulk scoop
   *   is superseded). Presentation-only; the sim's release/purge guards stay
   *   `> 6`, so a 6-bulk volley now sits EXACTLY at the guard boundary and must
   *   still pass (`6 > 6` is false — accepted). Pinned by "a 6-bulk Simple volley
   *   is accepted by the release guard" in Simulation.test.ts so the off-by-one
   *   can never regress silently.
   * - `portalCycle`: the six phase-aligned blue-anchor positions the entrance
   *   portal auto-moves through. Presentation-only (Engine consumes it).
   * - `dwellTicks`: chamber-dwell cook timer for this run (Simple = 720/12s vs
   *   the standard DWELL_TICKS 540/9s). KERNEL-READ.
   * - `overloadTicks`: continuous-jam furnace-overload timer (Simple = 2400/40s
   *   vs the standard OVERLOAD_TICKS 1800/30s). KERNEL-READ.
   * Official seeds omit `rules` entirely; the kernel falls back to the
   * DWELL_TICKS / OVERLOAD_TICKS constants.
   */
  rules?: {
    beamCapacity?: number;
    portalCycle?: readonly ArenaAnchor[];
    dwellTicks?: number;
    overloadTicks?: number;
  };
}

export interface TraceFrame {
  tick: number;
  moveX: number;
  moveZ: number;
  aimX: number;
  aimZ: number;
  buttons: number;
  actions?: SimulationAction[];
}

export type SimulationAction =
  | { type: "place"; endpoint: "blue" | "orange"; target: ArenaAnchor | ReceiverTier }
  | { type: "release"; pieces: TrashKind[]; route?: Route; delivered?: number }
  | { type: "red-detonate"; location: "arena" | "chamber" }
  | { type: "emergency-purge"; pieces: TrashKind[] }
  | { type: "furnace-contact" }
  // `side` = the region the subject occupies AFTER the crossing (LOCKED-DESIGN
  // 18.12). The Engine stamps it for PLAYER subjects only, from the same
  // chamberThresholdZ predicate the camera uses; absolute (not a toggle) so a
  // missed event self-heals on the next crossing. Trash crossings never set it.
  | { type: "portal-traversal"; subject: "player" | "trash"; id?: number; side?: "arena" | "chamber" }
  // A reject piece physically leaving the jam (LOCKED-DESIGN 18.12): into the
  // furnace maw intake (incinerated) or back to the arena/hatch (returned).
  // Zero-point recovery — never touches score/Chain/Grade. `disposition`
  // defaults to "incinerated". Trusted like release.pieces; replay parity is
  // the authority, not physical validation.
  | { type: "feed"; bulk: number; lane?: ReceiverTier; disposition?: "incinerated" | "returned" }
  // A thrown piece of a still-pending load PHYSICALLY crossed into the chamber
  // before its 3s resolution (LOCKED-DESIGN 18.14 / F8 direction C).
  // Upgrade-only: raises `delivered` toward pieces.length, never lowers it.
  // Trusted exactly like release.pieces / feed.bulk — replay parity is the authority.
  //
  // ANTI-CHEAT BOUND (LOCKED-DESIGN 18.14): the action can only raise
  // `delivered` to at most `pieces.length` — a clean delivery of the pieces the
  // `release` action already declared. It cannot mint pieces or bulk, cannot
  // change kinds/route/receiver multiplier, cannot touch Chain or Grade except
  // through the normal outcome path, cannot bypass jam or baffle whole-load
  // rejection, cannot resurrect a resolved load, and dedupes by body id.
  // Maximum exploit value = the gap between the dice floor and a guaranteed
  // clean load (~7% of safe loads, ~22% of technical) — a ceiling the generous
  // floor already reaches by luck. Documented abstraction: `delivered` is a
  // COUNT and `resolveLoad` credits `pieces.slice(0, delivered)`, so an upgrade
  // never changes WHICH index is credited for a given count.
  | { type: "arrive"; loadId: string; id: number }
  | { type: "abandon" };

export interface LedgerEvent {
  seq: number;
  tick: number;
  type: string;
  runId: string;
  loadId?: string;
  ticketId?: string;
  data: Record<string, string | number | boolean | null>;
}

export interface LoadRecord {
  id: string;
  releasedAt: number;
  resolvesAt: number;
  pieces: TrashKind[];
  route: Route;
  delivered: number;
  outcome: LoadOutcome;
  score: number;
  chainAtRelease: number;
  gradeAtRelease: number;
}

export interface RunMetrics {
  loads: { clean: number; partial: number; missed: number };
  routeUsage: Record<string, number>;
  ticketCompleted: number;
  ticketExpired: number;
  overflowPeak: number;
  redDetonations: number;
  jams: number;
  emergencyPurges: number;
  portalPlacements: number;
  /** Total reject bulk hand-fed into the maw to clear jams (LOCKED-DESIGN 18.12). */
  jamBulkFed: number;
  /**
   * DISTINCT loads whose `delivered` was raised at least once by a physical
   * arrival (LOCKED-DESIGN 18.14 / F8 direction C). This is the "how many loads
   * did aim actually rescue" number the next dogfood should read: a two-piece
   * load carried missed → partial → clean counts ONCE here (and twice in
   * `upgradesApplied`).
   */
  loadsUpgraded: number;
  /**
   * Physical-arrival upgrade EVENTS: every `arrive` action that actually RAISED
   * a load's `delivered`, i.e. one per piece credited by aim rather than by the
   * dice floor. Always ≥ `loadsUpgraded` (AGENTS.md telemetry standard).
   */
  upgradesApplied: number;
}

export interface PerformanceSample {
  tick: number;
  expectedMs: number;
  actualMs: number;
  driftMs: number;
  fps: number;
}

export interface RunBundle {
  schema: 1;
  gameVersion: string;
  createdAt: string;
  runId: string;
  seed: GameSeed;
  practicePhase: number;
  trace: TraceFrame[];
  ledger: LedgerEvent[];
  loads: LoadRecord[];
  metrics: RunMetrics;
  performance: PerformanceSample[];
  failures: Array<{ tick: number; cause: string; detail?: string }>;
  notes?: string;
  /**
   * Unranked mode marker (LOCKED-DESIGN 18.11). Present only on Simple-mode
   * runs (mode "simple", derived from seed.ruleset); omitted on standard runs
   * so ranked bundles stay byte-identical. Honesty rider: an exported Simple
   * bundle declares itself unranked without introspecting seed.ruleset.
   */
  mode?: "standard" | "simple";
  /**
   * Simple-only "jams never occur" run option (LOCKED-DESIGN 18.14 / F4).
   * Present only when the toggle was ON and the seed's ruleset is "simple";
   * omitted otherwise so every ranked bundle stays byte-identical. Replay and
   * authoritative validation both thread it back into the Simulation, so a
   * toggled run can never self-quarantine as a determinism mismatch.
   */
  jamsDisabled?: true;
  final: {
    score: number;
    grade: number;
    chain: number;
    missedLoads: number;
    tick: number;
    reason: RunEndReason;
    ledgerHash: string;
  };
}

export const TRASH: Record<TrashKind, { bulk: number; value: number }> = {
  crate: { bulk: 1, value: 100 },
  can: { bulk: 2, value: 250 },
  green: { bulk: 3, value: 450 },
  red: { bulk: 3, value: 600 },
  husk: { bulk: 3, value: 150 },
};

export const RECEIVER_MULTIPLIER: Record<ReceiverTier, number> = {
  safe: 100,
  technical: 125,
  hazard: 150,
};

export const CHAIN_MULTIPLIER = [100, 150, 200, 250, 300] as const;

export function gradeMultiplier(grade: number): number {
  if (grade >= 85) return 150;
  if (grade >= 65) return 125;
  if (grade >= 40) return 110;
  return 100;
}

export function bulkOf(kinds: readonly TrashKind[]): number {
  return kinds.reduce((sum, kind) => sum + TRASH[kind].bulk, 0);
}
